import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  CheckCircle, 
  Building2, 
  Briefcase, 
  Bot, 
  Phone, 
  Rocket,
  ArrowRight,
  ArrowLeft,
  SkipForward
} from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || '';

const INDUSTRIES = [
  { id: "reception", name: "Reception & Front Desk", icon: "🏢" },
  { id: "sales", name: "Sales & Outbound", icon: "📞" },
  { id: "support", name: "Customer Support", icon: "🎧" },
  { id: "healthcare", name: "Healthcare", icon: "🏥" },
  { id: "real_estate", name: "Real Estate", icon: "🏠" },
  { id: "legal", name: "Legal Services", icon: "⚖️" },
  { id: "custom", name: "Custom / Other", icon: "⚙️" }
];

const STEPS = [
  { id: 1, name: "Business Details", icon: Building2 },
  { id: 2, name: "Industry", icon: Briefcase },
  { id: 3, name: "Create Agent", icon: Bot },
  { id: 4, name: "Phone Setup", icon: Phone },
  { id: 5, name: "Complete", icon: Rocket }
];

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [status, setStatus] = useState(null);
  
  // Form data
  const [businessName, setBusinessName] = useState("");
  const [industry, setIndustry] = useState("");
  const [agentName, setAgentName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  useEffect(() => {
    loadOnboardingStatus();
  }, []);

  const loadOnboardingStatus = async () => {
    const token = localStorage.getItem("speakr_token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/onboarding/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
        setCurrentStep(data.current_step || 1);
        setBusinessName(data.business?.name || "");
        setIndustry(data.business?.industry || "");
        
        // If already completed, redirect to dashboard
        if (data.completed) {
          navigate("/dashboard");
        }
      }
    } catch (error) {
      console.error("Failed to load onboarding status:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveStep = async (step, data = {}) => {
    const token = localStorage.getItem("speakr_token");
    setSaving(true);
    
    try {
      const res = await fetch(`${API_URL}/api/onboarding/step`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ step, data })
      });
      
      if (res.ok) {
        setCurrentStep(step);
        return true;
      }
    } catch (error) {
      console.error("Failed to save step:", error);
      toast.error("Failed to save progress");
    } finally {
      setSaving(false);
    }
    return false;
  };

  const handleNext = async () => {
    let stepData = {};
    
    switch (currentStep) {
      case 1:
        if (!businessName.trim()) {
          toast.error("Please enter your business name");
          return;
        }
        stepData = { name: businessName };
        break;
      case 2:
        if (!industry) {
          toast.error("Please select your industry");
          return;
        }
        stepData = { industry };
        break;
      case 3:
        // Agent creation is optional in wizard
        break;
      case 4:
        stepData = { phone_number: phoneNumber };
        break;
    }
    
    const nextStep = currentStep + 1;
    const saved = await saveStep(nextStep, stepData);
    
    if (saved && nextStep >= 5) {
      toast.success("Welcome to Speakr! 🎉");
      navigate("/dashboard");
    }
  };

  const handleSkip = async () => {
    const token = localStorage.getItem("speakr_token");
    
    try {
      const res = await fetch(`${API_URL}/api/onboarding/skip`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Onboarding skipped. You can complete setup anytime!");
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Failed to skip onboarding:", error);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 py-12 px-4" data-testid="onboarding-wizard">
      <div className="max-w-2xl mx-auto">
        {/* Progress Steps */}
        <div className="flex justify-between mb-8">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isComplete = currentStep > step.id;
            
            return (
              <div key={step.id} className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  isComplete ? "bg-green-500" : isActive ? "bg-blue-500" : "bg-slate-700"
                }`}>
                  {isComplete ? (
                    <CheckCircle className="w-5 h-5 text-white" />
                  ) : (
                    <Icon className="w-5 h-5 text-white" />
                  )}
                </div>
                <span className={`text-xs mt-2 ${isActive ? "text-white" : "text-slate-500"}`}>
                  {step.name}
                </span>
              </div>
            );
          })}
        </div>

        {/* Step Content */}
        <Card className="border-slate-700 bg-slate-800/50">
          <CardHeader>
            <CardTitle className="text-white text-2xl">
              {currentStep === 1 && "Let's set up your business"}
              {currentStep === 2 && "What industry are you in?"}
              {currentStep === 3 && "Create your first AI agent"}
              {currentStep === 4 && "Set up your phone number"}
              {currentStep === 5 && "You're all set!"}
            </CardTitle>
            <CardDescription className="text-slate-400">
              {currentStep === 1 && "Tell us about your business so we can customize your experience."}
              {currentStep === 2 && "We'll configure your AI agents based on your industry."}
              {currentStep === 3 && "Your AI agent will handle calls on your behalf."}
              {currentStep === 4 && "Connect a phone number to start receiving calls."}
              {currentStep === 5 && "Your Speakr account is ready to use."}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Step 1: Business Details */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">Business Name</Label>
                  <Input
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    placeholder="Acme Inc."
                    className="bg-slate-900 border-slate-600 text-white"
                    data-testid="onboarding-business-name"
                  />
                </div>
              </div>
            )}

            {/* Step 2: Industry Selection */}
            {currentStep === 2 && (
              <div className="grid grid-cols-2 gap-3">
                {INDUSTRIES.map((ind) => (
                  <button
                    key={ind.id}
                    onClick={() => setIndustry(ind.id)}
                    className={`p-4 rounded-lg border text-left transition-all ${
                      industry === ind.id
                        ? "border-blue-500 bg-blue-500/20"
                        : "border-slate-600 hover:border-slate-500 bg-slate-900"
                    }`}
                    data-testid={`industry-${ind.id}`}
                  >
                    <span className="text-2xl">{ind.icon}</span>
                    <p className="text-white mt-2 text-sm">{ind.name}</p>
                  </button>
                ))}
              </div>
            )}

            {/* Step 3: Agent Creation (Simplified) */}
            {currentStep === 3 && (
              <div className="space-y-4">
                <p className="text-slate-400">
                  We've already created a default AI agent based on your industry. 
                  You can customize it later from your dashboard.
                </p>
                <div className="p-4 bg-slate-900 rounded-lg border border-slate-700">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                      <Bot className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">AI Receptionist</p>
                      <p className="text-slate-400 text-sm">Ready to handle calls</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Phone Setup */}
            {currentStep === 4 && (
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">Phone Number (Optional)</Label>
                  <Input
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    className="bg-slate-900 border-slate-600 text-white"
                    data-testid="onboarding-phone"
                  />
                  <p className="text-slate-500 text-sm mt-2">
                    You can set this up later from your dashboard settings.
                  </p>
                </div>
              </div>
            )}

            {/* Step 5: Complete */}
            {currentStep === 5 && (
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Rocket className="w-10 h-10 text-green-400" />
                </div>
                <h3 className="text-xl text-white font-semibold mb-2">
                  Welcome to Speakr! 🎉
                </h3>
                <p className="text-slate-400">
                  Your AI voice agent is ready to start handling calls.
                </p>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between pt-4">
              <div>
                {currentStep > 1 && currentStep < 5 && (
                  <Button
                    variant="ghost"
                    onClick={handleBack}
                    className="text-slate-400"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                )}
              </div>
              
              <div className="flex gap-3">
                {currentStep < 5 && (
                  <Button
                    variant="ghost"
                    onClick={handleSkip}
                    className="text-slate-400"
                  >
                    <SkipForward className="w-4 h-4 mr-2" />
                    Skip Setup
                  </Button>
                )}
                
                <Button
                  onClick={handleNext}
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="onboarding-next"
                >
                  {saving ? "Saving..." : currentStep >= 5 ? "Go to Dashboard" : "Continue"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
